import { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import StatefulTabs from '~/ui/components/common/StatefulTabs';
import Modal from '~/ui/components/common/Modal';
import api from '~/services/api';
import { PatientStatus, StorageFileArea } from '~/services/api/enums';
import { extractErrorMessage } from '~/utils/error';
import usePatientEnrollmentOptions from '~/ui/components/providers/PatientEnrollmentProvider/usePatientEnrollment';
import { useStoreActions } from '~/store/hooks';

import { IPatient, IPatientCreate } from '~/services/api/patients/types';
import { IPatientCreateFormValues } from '../../types';
import DevicesForm from '../DevicesForm';
import PatientForm from '../PatientForm';
import Button from '~/ui/components/common/Button';
import { isoToFormat } from '~/utils/date';

import styles from './AddPatientModal.module.scss';
import { IInsurance } from '~/services/api/insurances/types';
import { DEVICES_DOCS_LABEL, GENERAL_INFO_LABEL } from '../../constants';

interface IProps {
  onClose: () => void;
}

const AddPatientModal: FC<IProps> = ({ onClose }) => {
  const [isPatientAdding, setIsPatientAdding] = useState(false);

  const { showNotify, showError } = useStoreActions(actions => actions.snackbar);
  const onGetDashboardCounters = useStoreActions(
    actions => actions.dashboard.onGetDashboardCounters,
  );

  const {
    setPrograms,
    setInsuranceSelectors,
    setStorageFilesSelectors,
    setManagers,
    setDevices,
    managers,
  } = usePatientEnrollmentOptions();

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IPatientCreateFormValues>();

  const { programId } = watch();

  const getDropdownOptions = async () => {
    if (!programId) {
      setManagers([]);
      setDevices([]);
      return;
    }

    const selectors = await api.patients.getSelectorsById(0, programId).then(res => res.data);

    setManagers(selectors.users);
    setDevices(selectors.devices);
  };

  useEffect(() => {
    getDropdownOptions();
    setValue('healthCareManagerIds', []);
  }, [programId]);

  const onMount = async () => {
    try {
      const selectors = await api.patients.getSelectorsById(0).then(res => res.data);

      const storageFileSelectors = await api.storageFiles
        .getSelectorsByArea(StorageFileArea.PatientDocuments)
        .then(res => res.data);

      const insuranceSelectors = await api.insurances.getSelectors().then(res => res.data);

      setPrograms(selectors.programs);
      setStorageFilesSelectors(storageFileSelectors);
      setInsuranceSelectors(insuranceSelectors);
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const handleClose = () => {
    // prevent closing popup when patient is adding
    if (isPatientAdding) return;
    onClose();
  };

  const onSubmitForm = async (values: IPatientCreateFormValues) => {
    const deviceIds = [
      values.bloodPressureDeviceId,
      values.weightScaleDeviceId,
      values.pulseOximeterDeviceId,
      values.bloodGlucoseDeviceId,
    ].filter(deviceId => !!deviceId);

    const insuranceData = {
      ...values.insurance,
      expiresAt: values.insurance.expiresAt
        ? isoToFormat(values.insurance.expiresAt, 'y-MM-dd')
        : null,
      authorizationStartDate: isoToFormat(values.insurance.authorizationStartDate, 'y-MM-dd'),
      authorizationEndDate: isoToFormat(values.insurance.authorizationEndDate, 'y-MM-dd'),
    } as IInsurance;

    const patientData = {
      id: 0,
      status: PatientStatus.Approved,
      timezone: null,
      ...values,
      dateOfBirth: isoToFormat(values.dateOfBirth, 'y-MM-dd'),
      healthCareManagers: managers.filter(x => values.healthCareManagerIds.includes(x.id)),
      isHomeHealthCare: Boolean(Number(values.isHomeHealthCare)),
      shippingAddressSameAsHome: Boolean(Number(values.shippingAddressSameAsHome)),
    } as IPatient;

    const payload = {
      ...patientData,
      insurance: insuranceData,
      storageFiles: values.storageFiles,
    } as IPatientCreate;

    setIsPatientAdding(true);

    try {
      await api.patients.create(values.programId, payload, deviceIds);

      onGetDashboardCounters(); // update patients counter
      showNotify('Patient successfully enrolled');
      onClose();
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsPatientAdding(false);
    }
  };

  const tabs = [
    {
      label: GENERAL_INFO_LABEL,
      value: GENERAL_INFO_LABEL,
      component: <PatientForm />,
      inputNames: [
        'programId',
        'healthCareManagerIds',
        'subjectId',
        'firstName',
        'middleName',
        'lastName',
        'phoneNumber',
        'dateOfBirth',
        'ethnicity',
        'dialingNote',
        'isHomeHealthCare',
        'gender',
        'country',
        'state',
        'city',
        'address',
        'zip',
        'comment',
        'homeHealthcarePeople',
        'insurance',
        'uniquePhone',
      ],
    },
    {
      label: DEVICES_DOCS_LABEL,
      value: DEVICES_DOCS_LABEL,
      component: <DevicesForm />,
      inputNames: [
        'bloodPressureDeviceId',
        'weightScaleDeviceId',
        'pulseOximeterDeviceId',
        'bloodGlucoseDeviceId',
        'caretakers',
        'storageFiles',
        'uniquePhone',
      ],
    },
  ];

  useEffect(() => {
    onMount();
  }, []);

  return (
    <Modal
      open
      title="Patient Enrollment"
      className={styles.modal}
      onClose={handleClose}
      footer={
        <div className={styles.saveBtnWrapper}>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            isLoading={isPatientAdding}
            disabled={isPatientAdding}
            onClick={handleSubmit(onSubmitForm)}
          >
            Submit
          </Button>
        </div>
      }
    >
      <StatefulTabs tabs={tabs} errors={errors} />
    </Modal>
  );
};

export default AddPatientModal;
